import { HttpHeaders, HttpContext, HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BypassInterceptorContext } from 'src/app/interceptors/http.interceptor';
import {
  NotificationResponseType,
  NotificationsData,
  SystemUpdateLogData,
  SystemUpdateLogResponseType,
} from 'src/app/types/response/notification.response.type';
import { environment } from 'src/environments/environment';
import { formatContentToHtml } from 'src/app/libs/functions/format-content.functions';

@Component({
  selector: 'app-notif-drawer',
  standalone: false,
  templateUrl: './notif-drawer.component.html',
  styleUrl: './notif-drawer.component.scss',
})
export class NotifDrawerComponent implements OnDestroy {
  destroy$ = new Subject();
  @ViewChild('systemLogScrollContainer') systemLogScrollContainer?: ElementRef<HTMLElement>;
  private systemLogSubscription?: Subscription;
  @Input() isVisibleNotif = false;
  @Output() isVisibleNotifEvent = new EventEmitter<boolean>();
  @Output() totalNotifChange = new EventEmitter<number>();

  notification: NotificationsData = {
    total: 0,
    limit: 0,
    page: 0,
    notifications: [],
  };
  systemUpdateLog: SystemUpdateLogData = {
    total: 0,
    page: 0,
    limit: 0,
    data: [],
  };

  allSystemUpdateLogs: SystemUpdateLogResponseType[] = [];
  systemLogPage = 1;
  systemLogLoading = false;
  systemLogHasMore = true;

  unreadNotif: NotificationResponseType[] = [];
  unreadSystemUpdateLog: SystemUpdateLogResponseType[] = [];

  selectedTabIndex = 0;
  drawerWidth: string = window.innerWidth <= 650 ? '100vw' : '650px';
  drawerBodyStyle = {
    display: 'flex',
    'flex-direction': 'column',
    overflow: 'hidden',
    height: '100%',
    'padding-top': '16px',
  };

  @HostListener('window:resize')
  onResize(): void {
    this.drawerWidth = window.innerWidth <= 650 ? '100vw' : '650px';
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.systemLogSubscription?.unsubscribe();
  }

  constructor(
    private message: NzMessageService,
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.getNotif();
    this.resetSystemUpdateLog();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isVisibleNotif'] &&
      changes['isVisibleNotif'].currentValue === true
    ) {
      this.getNotif();
      this.resetSystemUpdateLog();
    }
  }

  // ---------------------------------------------------------------------------
  // Drawer actions
  // ---------------------------------------------------------------------------

  close(): void {
    this.isVisibleNotif = false;
    this.isVisibleNotifEvent.emit(this.isVisibleNotif);
  }

  closeDrawer(): void {
    this.isVisibleNotif = false;
    this.isVisibleNotifEvent.emit(false);
  }

  // ---------------------------------------------------------------------------
  // Tab-aware header helpers
  // ---------------------------------------------------------------------------

  get isMarkAllDisabled(): boolean {
    return this.selectedTabIndex === 0
      ? !this.notification || this.notification.total === 0
      : this.allSystemUpdateLogs.length === 0;
  }

  handleMarkAllRead(): void {
    if (this.selectedTabIndex === 0) {
      this.readAllNotif();
    } else {
      this.readAllSysUpdateLog();
    }
  }

  // ---------------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------------

  getNotif(): void {
    const apiUrl = this.buildApiUrl('/user/api/v1/notification');

    this.httpClient
      .get<{ data: NotificationsData }>(apiUrl, {
        headers: this.getAuthHeaders(),
        context: this.createContext(),
      })
      .subscribe({
        next: (res) => {
          this.notification = res.data;
          this.unreadNotif = res.data.notifications.filter(
            (item) => item.is_read === false
          );
          this.emitTotalUnread();
        },
        error: (error) => {
          console.error('Error fetching notifications:', error);
        },
      });
  }

  readNotif(id: number): void {
    const notification = this.notification.notifications.find(
      (item) => item.id === id
    );
    if (!notification || notification.is_read) return;

    const apiUrl = this.buildApiUrl(`/user/api/v1/notification/${id}/read`);

    this.httpClient
      .put<any>(apiUrl, {
        headers: this.getAuthHeaders(),
        context: this.createContext(),
      })
      .subscribe({
        next: () => this.getNotif(),
        error: (error) => this.showError(error, 'An error occurred while updating the notification.'),
      });
  }

  readAllNotif(): void {
    if (this.unreadNotif.length === 0) return;

    const apiUrl = this.buildApiUrl('/user/api/v1/notification/read-all');

    this.httpClient
      .put<any>(apiUrl, {
        headers: this.getAuthHeaders(),
        context: this.createContext(),
      })
      .subscribe({
        next: () => this.getNotif(),
        error: (error) => this.showError(error, 'An error occurred while updating the notification.'),
      });
  }

  // ---------------------------------------------------------------------------
  // System Update Logs
  // ---------------------------------------------------------------------------

  resetSystemUpdateLog(): void {
    this.allSystemUpdateLogs = [];
    this.systemLogPage = 1;
    this.systemLogHasMore = true;
    this.systemLogLoading = false;
    this.getSystemUpdateLog();
  }

  getSystemUpdateLog(): void {
    if (this.systemLogLoading || !this.systemLogHasMore) return;

    // Cancel any in-flight request before starting a new one
    if (this.systemLogSubscription) {
      this.systemLogSubscription.unsubscribe();
      this.systemLogSubscription = undefined;
    }

    this.systemLogLoading = true;

    const apiUrl = this.buildApiUrl('/user/api/v1/user/system-update-log');
    const params = { limit: 10, page: this.systemLogPage };

    this.systemLogSubscription = this.httpClient
      .get<{ data: SystemUpdateLogData }>(apiUrl, {
        headers: this.getAuthHeaders(),
        params,
        context: this.createContext(),
      })
      .pipe(
        finalize(() => {
          // Guarantees loading is reset even if the subscription is unsubscribed
          this.systemLogLoading = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.systemUpdateLog = res.data;
          this.allSystemUpdateLogs = [...this.allSystemUpdateLogs, ...res.data.data];
          this.systemLogPage++;
          const totalPages = Math.ceil(res.data.total / res.data.limit);
          this.systemLogHasMore = res.data.page < totalPages;

          this.unreadSystemUpdateLog = this.allSystemUpdateLogs.filter(
            (item) => item.is_read === false
          );
          this.emitTotalUnread();
          this.checkAndLoadMoreIfNoScroll();
        },
        error: (error) => {
          console.error('Error fetching system update logs:', error);
        },
      });
  }

  checkAndLoadMoreIfNoScroll(): void {
    // Wait for Angular to render the new items into the DOM
    setTimeout(() => {
      if (!this.systemLogScrollContainer) return;
      const element = this.systemLogScrollContainer.nativeElement;
      // If the container is not scrollable yet and there's more data, load another page
      if (
        element.scrollHeight <= element.clientHeight &&
        this.systemLogHasMore
      ) {
        this.getSystemUpdateLog();
      }
    }, 200);
  }

  onSystemLogScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const threshold = 100;
    const atBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight <= threshold;
    if (atBottom) {
      this.getSystemUpdateLog();
    }
  }

  readSysUpdateLog(id: number): void {
    const log = this.allSystemUpdateLogs.find((item) => item.id === id);
    if (!log || log.is_read) return;

    const apiUrl = this.buildApiUrl(`/user/api/v1/user/system-update-log/${id}/read`);

    this.httpClient
      .post<any>(apiUrl, {
        headers: this.getAuthHeaders(),
        context: this.createContext(),
      })
      .subscribe({
        next: () => this.resetSystemUpdateLog(),
        error: (error) => this.showError(error, 'An error occurred while updating the system update log.'),
      });
  }

  readAllSysUpdateLog(): void {
    if (this.unreadSystemUpdateLog.length === 0) return;

    const apiUrl = this.buildApiUrl('/user/api/v1/user/system-update-log/read-all');

    this.httpClient
      .post<any>(apiUrl, {
        headers: this.getAuthHeaders(),
        context: this.createContext(),
      })
      .subscribe({
        next: () => this.resetSystemUpdateLog(),
        error: (error) => this.showError(error, 'An error occurred while updating the system update log.'),
      });
  }

  // ---------------------------------------------------------------------------
  // Content formatting
  // ---------------------------------------------------------------------------

  formatContent(content: string): SafeHtml {
    const formattedHtml = formatContentToHtml(content);
    const sanitizedHtml =
      this.sanitizer.sanitize(SecurityContext.HTML, formattedHtml) || '';
    return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  // ---------------------------------------------------------------------------
  // Notification action mapping
  // ---------------------------------------------------------------------------

  private readonly BALANCE_ACTION = {
    label: 'Top up balance →',
    link: '/finance/balance',
  } as const;

  private readonly UPGRADE_ACTION = {
    label: 'Upgrade Package →',
    link: '/subscriptions/upgrade-package',
  } as const;

  private readonly actionMap: Record<string, typeof this.BALANCE_ACTION | typeof this.UPGRADE_ACTION> = {
    'Top Up Required — Auto Renewal At Risk': this.BALANCE_ACTION,
    '3 Days Left — Balance Still Insufficient': this.BALANCE_ACTION,
    'Your Package has been Downgraded': this.UPGRADE_ACTION,
    'Final Day Alert!': this.UPGRADE_ACTION,
    "Time's Ticking — Let's Get You Set!": this.UPGRADE_ACTION,
    "You're Still Covered, But Not for Long!": this.UPGRADE_ACTION,
  };

  actionSubsTitle(title: string): string {
    return this.actionMap[title]?.label ?? '';
  }

  actionSubsLink(title: string): string {
    return this.actionMap[title]?.link ?? '';
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private buildApiUrl(endpoint: string): string {
    return environment.SWITCH_URL + endpoint;
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth-token') || '';
    return new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  private createContext(): HttpContext {
    return new HttpContext().set(BypassInterceptorContext, true);
  }

  private emitTotalUnread(): void {
    this.totalNotifChange.emit(
      this.unreadNotif.length + this.unreadSystemUpdateLog.length
    );
  }

  private showError(error: any, fallbackMessage: string): void {
    this.message.create(
      'error',
      error?.data?.errors || fallbackMessage
    );
  }
}
